import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Cookie, X, Settings, Check } from "lucide-react";

const CookieBanner = () => {
  const [showBanner, setShowBanner] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [preferences, setPreferences] = useState({
    necessary: true,
    analytics: false,
    marketing: false,
  });

  useEffect(() => {
    const consent = localStorage.getItem("cookieConsent");
    if (!consent) {
      const timer = setTimeout(() => setShowBanner(true), 1500);
      return () => clearTimeout(timer);
    }
  }, []);

  // Allow footer "Cookie Settings" link to re-open the preferences panel
  useEffect(() => {
    const handler = () => { setShowSettings(true); setShowBanner(true); };
    window.addEventListener("openCookieSettings", handler);
    return () => window.removeEventListener("openCookieSettings", handler);
  }, []);

  // Update Google Consent Mode v2 and notify GoogleAdSense component
  const applyConsent = (consent) => {
    localStorage.setItem("cookieConsent", JSON.stringify(consent));
    if (typeof window.gtag === "function") {
      window.gtag("consent", "update", {
        ad_storage:          consent.marketing ? "granted" : "denied",
        ad_user_data:        consent.marketing ? "granted" : "denied",
        ad_personalization:  consent.marketing ? "granted" : "denied",
        analytics_storage:   consent.analytics ? "granted" : "denied",
      });
    }
    window.dispatchEvent(new Event("cookieConsentUpdated"));
    setShowBanner(false);
  };

  const acceptAll = () => {
    applyConsent({
      necessary: true,
      analytics: true,
      marketing: true,
      timestamp: new Date().toISOString(),
    });
  };

  const acceptSelected = () => {
    applyConsent({
      ...preferences,
      timestamp: new Date().toISOString(),
    });
  };

  const rejectAll = () => {
    applyConsent({
      necessary: true,
      analytics: false,
      marketing: false,
      timestamp: new Date().toISOString(),
    });
  };

  return (
    <AnimatePresence>
      {showBanner && (
        <motion.div
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 100, opacity: 0 }}
          transition={{ type: "spring", damping: 25 }}
          className="fixed bottom-0 left-0 right-0 z-50 p-4 md:p-6"
          data-testid="cookie-banner"
        >
          <div className="max-w-6xl mx-auto">
            <div className="glass-card rounded-2xl p-6 md:p-8 border border-white/10 shadow-2xl">
              {!showSettings ? (
                /* Main Banner */
                <div className="flex flex-col lg:flex-row gap-6 items-start lg:items-center">
                  <div className="flex items-start gap-4 flex-1">
                    <div className="w-12 h-12 rounded-xl bg-emerald-500/20 flex items-center justify-center flex-shrink-0">
                      <Cookie className="w-6 h-6 text-emerald-400" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-white mb-2">
                        We Value Your Privacy
                      </h3>
                      <p className="text-gray-400 text-sm leading-relaxed">
                        We use cookies to enhance your browsing experience, serve personalized content, 
                        and analyze our traffic. By clicking "Accept All", you consent to our use of cookies. 
                        Read our{" "}
                        <a href="/privacy" className="text-emerald-400 hover:underline">
                          Privacy Policy
                        </a>{" "}
                        for more information.
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex flex-wrap gap-3 w-full lg:w-auto">
                    <button
                      onClick={() => setShowSettings(true)}
                      className="flex items-center gap-2 px-4 py-2.5 rounded-xl border border-white/10 text-gray-300 hover:bg-white/5 transition-colors text-sm font-medium"
                    >
                      <Settings className="w-4 h-4" />
                      Customize
                    </button>
                    <button
                      onClick={rejectAll}
                      className="px-4 py-2.5 rounded-xl border border-white/10 text-gray-300 hover:bg-white/5 transition-colors text-sm font-medium"
                    >
                      Reject All
                    </button>
                    <button
                      onClick={acceptAll}
                      className="btn-primary px-6 py-2.5 rounded-xl text-white text-sm font-medium"
                    >
                      Accept All
                    </button>
                  </div>
                </div>
              ) : (
                /* Settings Panel */
                <div>
                  <div className="flex items-center justify-between mb-6">
                    <h3 className="text-lg font-semibold text-white">Cookie Preferences</h3>
                    <button
                      onClick={() => setShowSettings(false)}
                      className="p-2 hover:bg-white/5 rounded-lg transition-colors"
                    >
                      <X className="w-5 h-5 text-gray-400" />
                    </button>
                  </div>
                  
                  <div className="space-y-4 mb-6">
                    {/* Necessary Cookies */}
                    <div className="flex items-center justify-between p-4 rounded-xl bg-white/5 border border-white/10">
                      <div>
                        <h4 className="text-white font-medium mb-1">Necessary Cookies</h4>
                        <p className="text-gray-400 text-sm">
                          Essential for the website to function properly. Cannot be disabled.
                        </p>
                      </div>
                      <div className="w-12 h-7 bg-emerald-500 rounded-full flex items-center justify-end px-1">
                        <div className="w-5 h-5 bg-white rounded-full" />
                      </div>
                    </div>
                    
                    {/* Analytics Cookies */}
                    <div className="flex items-center justify-between p-4 rounded-xl bg-white/5 border border-white/10">
                      <div>
                        <h4 className="text-white font-medium mb-1">Analytics Cookies</h4>
                        <p className="text-gray-400 text-sm">
                          Help us understand how visitors interact with our website.
                        </p>
                      </div>
                      <button
                        onClick={() => setPreferences(p => ({ ...p, analytics: !p.analytics }))}
                        className={`w-12 h-7 rounded-full flex items-center px-1 transition-colors ${
                          preferences.analytics ? "bg-emerald-500 justify-end" : "bg-gray-600 justify-start"
                        }`}
                      >
                        <div className="w-5 h-5 bg-white rounded-full" />
                      </button>
                    </div>
                    
                    {/* Marketing Cookies */}
                    <div className="flex items-center justify-between p-4 rounded-xl bg-white/5 border border-white/10">
                      <div>
                        <h4 className="text-white font-medium mb-1">Marketing Cookies</h4>
                        <p className="text-gray-400 text-sm">
                          Used to deliver personalized advertisements and track campaigns.
                        </p>
                      </div>
                      <button
                        onClick={() => setPreferences(p => ({ ...p, marketing: !p.marketing }))}
                        className={`w-12 h-7 rounded-full flex items-center px-1 transition-colors ${
                          preferences.marketing ? "bg-emerald-500 justify-end" : "bg-gray-600 justify-start"
                        }`}
                      >
                        <div className="w-5 h-5 bg-white rounded-full" />
                      </button>
                    </div>
                  </div>
                  
                  <div className="flex justify-end gap-3">
                    <button
                      onClick={rejectAll}
                      className="px-4 py-2.5 rounded-xl border border-white/10 text-gray-300 hover:bg-white/5 transition-colors text-sm font-medium"
                    >
                      Reject All
                    </button>
                    <button
                      onClick={acceptSelected}
                      className="btn-primary px-6 py-2.5 rounded-xl text-white text-sm font-medium flex items-center gap-2"
                    >
                      <Check className="w-4 h-4" />
                      Save Preferences
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default CookieBanner;
